import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertRule, AlertPreset, useAlertRules } from '../hooks/useAlertRules';
import { PresetPicker } from '../components/alerts/PresetPicker';
import { AlertBuilder } from '../components/alerts/AlertBuilder';
import { AlertReviewPanel } from '../components/alerts/AlertReviewPanel';
import { Button } from '../components/ui/Button';
import { ChevronRight, ChevronLeft, Check, X } from 'lucide-react';
import { cn } from '../components/ui/Button';
export function CreateAlertView() {
  const navigate = useNavigate();
  const {
    addRule
  } = useAlertRules();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedPreset, setSelectedPreset] = useState<AlertPreset | null>(null);
  const [draftRule, setDraftRule] = useState<Partial<AlertRule>>({
    delivery: {
      in_app: true,
      push: false
    }
  });
  // Generate a smart alert name from configured settings
  const generateAlertName = (preset: AlertPreset, rule: Partial<AlertRule>): string => {
    const details = rule.rule_details || {};
    switch (preset.notification_type) {
      case 'data_availability':
        {
          const modelCount = details.model_scope?.length || 0;
          const modelPhrase = modelCount === 0 ? 'All Models' : modelCount === 1 ? details.model_scope![0] : `${modelCount} Models`;
          return `Forecast Update — ${modelPhrase}`;
        }
      case 'threshold':
        {
          const cond = details.conditions?.[0];
          if (!cond) return preset.name;
          const locCount = details.locations?.length || 0;
          const locPhrase = locCount === 0 ? '' : locCount === 1 ? ` — ${details.locations![0]}` : ` — ${locCount} Locations`;
          return `${cond.variable_display_name} ${cond.operator} ${cond.threshold}${cond.unit}${locPhrase}`;
        }
      case 'correction':
        {
          const cond = details.conditions?.[0];
          if (!cond) return preset.name;
          return `Big Forecast Change — ${cond.variable_display_name} ${cond.threshold}${cond.unit}`;
        }
      case 'model_divergence':
        {
          const cond = details.conditions?.[0];
          if (!cond) return preset.name;
          const modelCount = details.models?.length || 0;
          const modelPhrase = modelCount === 0 ? 'All Models' : `${modelCount} Models`;
          return `Models Disagree — ${cond.variable_display_name} > ${cond.threshold}${cond.unit} (${modelPhrase})`;
        }
      default:
        return preset.name;
    }
  };
  const handleSelectPreset = (preset: AlertPreset) => {
    setSelectedPreset(preset);
    setDraftRule(prev => ({
      ...prev,
      preset_id: preset.id,
      notification_type: preset.notification_type,
      rule_details: {
        ...preset.default_details
      }
    }));
    setStep(2);
  };
  const handleCreate = () => {
    if (!draftRule.alert_name || !selectedPreset) return;
    const newRule: AlertRule = {
      id: Date.now().toString(),
      alert_name: draftRule.alert_name,
      notification_type: selectedPreset.notification_type,
      workspace_id: draftRule.workspace_id,
      workspace_name: draftRule.workspace_name,
      rule_details: draftRule.rule_details!,
      delivery: draftRule.delivery!,
      preset_id: selectedPreset.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    addRule(newRule);
    navigate('/notifications');
  };
  return <div className="jua-page-bg min-h-screen p-6 md:p-8" data-id="element-2189">
      <div className="max-w-5xl mx-auto space-y-8" data-id="element-2190">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-2191">
          <div data-id="element-2192">
            <h1 className="text-2xl font-bold text-white" data-id="element-2193">Create New Alert</h1>
            <p className="text-slate-400 mt-1" data-id="element-2194">
              Configure automated notifications for market events and model
              updates.
            </p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/notifications')} className="text-slate-400 hover:text-white" data-id="element-2195">
            <X size={20} className="mr-2" data-id="element-2196" />
            Cancel
          </Button>
        </div>

        {/* Stepper */}
        <div className="relative" data-id="element-2197">
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-slate-800 -z-10" data-id="element-2198" />
          <div className="flex justify-between max-w-2xl mx-auto" data-id="element-2199">
            {[{
            num: 1,
            label: 'Choose Preset'
          }, {
            num: 2,
            label: 'Configure'
          }, {
            num: 3,
            label: 'Review'
          }].map(s => {
            const isActive = step >= s.num;
            const isCurrent = step === s.num;
            return <div key={s.num} className="flex flex-col items-center gap-2 bg-[#0B1120] px-4" data-id="element-2200">
                  <div className={cn('w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300', isActive ? 'bg-pink-500 text-white shadow-[0_0_15px_rgba(236,72,153,0.4)]' : 'bg-slate-800 text-slate-500')} data-id="element-2201">
                    {isActive ? <Check size={14} data-id="element-2202" /> : s.num}
                  </div>
                  <span className={cn('text-xs font-medium transition-colors', isCurrent ? 'text-white' : 'text-slate-500')} data-id="element-2203">
                    {s.label}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Content */}
        <div className="min-h-[400px]" data-id="element-2204">
          {step === 1 && <PresetPicker onSelect={handleSelectPreset} selectedId={selectedPreset?.id} data-id="element-2205" />}

          {step === 2 && selectedPreset && <AlertBuilder preset={selectedPreset} rule={draftRule} onChange={updates => setDraftRule(prev => ({
          ...prev,
          ...updates
        }))} data-id="element-2206" />}

          {step === 3 && selectedPreset && <AlertReviewPanel preset={selectedPreset} rule={draftRule} onChange={updates => setDraftRule(prev => ({
          ...prev,
          ...updates
        }))} data-id="element-2207" />}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-8 border-t border-slate-800" data-id="element-2208">
          <Button variant="secondary" onClick={() => {
          if (step === 2) {
            // Go back to step 1 and clear preset so user sees category picker
            setSelectedPreset(null);
            setStep(1);
          } else {
            setStep(prev => Math.max(1, prev - 1) as 1 | 2 | 3);
          }
        }} disabled={step === 1} className={step === 1 ? 'opacity-0 pointer-events-none' : ''} data-id="element-2209">
            <ChevronLeft size={16} className="mr-2" data-id="element-2210" />
            Back
          </Button>

          {step < 3 ? <Button onClick={() => {
          const nextStep = Math.min(3, step + 1) as 1 | 2 | 3;
          // Prefill alert name when entering Review step
          if (nextStep === 3 && selectedPreset && !draftRule.alert_name) {
            setDraftRule(prev => ({
              ...prev,
              alert_name: generateAlertName(selectedPreset, prev)
            }));
          }
          setStep(nextStep);
        }} disabled={step === 1 && !selectedPreset} data-id="element-2211">
              Next Step
              <ChevronRight size={16} className="ml-2" data-id="element-2212" />
            </Button> : <Button onClick={handleCreate} disabled={!draftRule.alert_name} className="px-8" data-id="element-2213">
              Create Alert
              <Check size={16} className="ml-2" data-id="element-2214" />
            </Button>}
        </div>
      </div>
    </div>;
}